import {
    Children,
    isValidElement,
    type ReactElement,
    type ReactNode,
    useMemo,
    useRef,
} from "react";
import { Screen } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";

import { BTDropdown } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { BdsButton, BdsIconDotsThree, BuiBreadcrumb } from "@buildertrend/components";
import { type RefType } from "@buildertrend/components/src/lib/types/RefType";

import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";

import { type IBTBreadcrumbProps } from "./BTBreadcrumb";

interface ICollapsedBreadcrumbItemProps {
    testid: string;
    separator: ReactNode;
    collapsedItems: ReactElement<{ children?: ReactNode }>[];
}

/**
 * Collapsed breadcrumb item that shows middle items in a dropdown menu.
 * BTDropdown handles keyboard accessibility (focus management, Escape) internally.
 * triggerRef enables focus return to the button when the menu closes.
 */
const CollapsedBreadcrumbItem = ({
    testid,
    separator,
    collapsedItems,
}: ICollapsedBreadcrumbItemProps) => {
    const triggerRef = useRef<HTMLElement>(null);

    const menu = (
        <BTMenu testid={`${testid}-menu`}>
            {collapsedItems.map((child, index) => (
                <BTMenu.Item key={index} testid={`${testid}-item-${index}`}>
                    {child.props.children}
                </BTMenu.Item>
            ))}
        </BTMenu>
    );

    return (
        <BuiBreadcrumb.Item testid={testid} separator={separator}>
            <BTDropdown
                overlay={menu}
                trigger={["click"]}
                triggerRef={triggerRef}
                testid={`${testid}-dropdown`}
            >
                <BdsButton
                    innerRef={triggerRef as RefType}
                    displayType="tertiary"
                    testid={`${testid}-trigger`}
                    icon={<BdsIconDotsThree />}
                    ariaLabel="Show collapsed breadcrumbs"
                />
            </BTDropdown>
        </BuiBreadcrumb.Item>
    );
};

/**
 * Hook that manages BTBreadcrumb state and responsive collapse behavior.
 * Returns props for the nav wrapper, BuiBreadcrumb, and processed children.
 * @param props - BTBreadcrumb props
 */
export const useBTBreadcrumb = (props: IBTBreadcrumbProps) => {
    const {
        children,
        testid,
        separator = "/",
        className,
        id,
        "aria-label": ariaLabel = "Breadcrumb",
    } = props;
    const useMdBreakpoint = useMediaQuery(`(max-width: ${Screen.md}px)`);

    // Process children for collapse behavior
    const processedChildren = useMemo((): ReactNode => {
        const childArray = Children.toArray(children).filter(
            isValidElement<{ children?: ReactNode }>,
        );

        if (useMdBreakpoint && childArray.length >= 3) {
            const first = childArray[0];
            const last = childArray[childArray.length - 1];
            const middle = childArray.slice(1, -1);

            const collapsedItem = (
                <CollapsedBreadcrumbItem
                    key="collapsed"
                    testid={`${testid}-collapsed`}
                    separator={separator}
                    collapsedItems={middle}
                />
            );

            return [first, collapsedItem, last];
        }

        return childArray;
    }, [children, useMdBreakpoint, testid, separator]);

    return {
        buiProps: {
            testid,
            separator,
            className: "bui-breadcrumb-container",
        },
        navProps: {
            id,
            className: cn("bui-breadcrumb", className),
            "aria-label": ariaLabel,
        },
        processedChildren,
    };
};
